import React, { useState, useEffect, useRef } from 'react'
import { makeStyles, withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import {
	Button, TextField, Chip, RadioGroup, Radio, FormControlLabel, FormControl, InputLabel, Select, MenuItem,
	Stepper, Step, StepLabel, StepContent, Grid, TablePagination
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import VisibilityIcon from '@material-ui/icons/Visibility';
import GetAppIcon from '@material-ui/icons/GetApp';
import { Modal } from 'antd';
import { CSVLink } from 'react-csv';
import HttpService from '../../../services/httpService';
import { connect } from "react-redux";
import { setLoading, setSnackInfo } from '../../../redux/actions/appAction';
import moment from 'moment';

const CSV_HEADERS = [
	{ key: "project", label: "Project" },
	{ key: "orderNumber", label: "PO/SO Number" },
	{ key: "type", label: "Type" },
	{ key: "amount", label: "Amount" },
	{ key: "status", label: "Status" },
	{ key: "createdBy", label: "Created By" },
	{ key: "pendingApprovalRole", label: "Pending Approval Role" },
	{ key: "createdDate", label: "Created Date" },
]

const useStyles = makeStyles((theme) => ({
	table: {
		minWidth: 1000,
	},
	paper: {
		padding: theme.spacing(2),
		marginBottom: 20,
	},
	headerRow: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		flexWrap: "wrap",
		gap: 10,
	},
	filters: {
		display: "flex",
		alignItems: "center",
		flexWrap: "wrap",
		gap: 20,
	},
}));

const StyledTableCell = withStyles((theme) => ({
	head: {
		backgroundColor: "#1C375D",
		color: "#fff",
		fontWeight: 600,
		whiteSpace: "nowrap",
	},
	body: {
		fontSize: 14,
	},
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
	root: {
		'&:nth-of-type(odd)': {
			backgroundColor: theme.palette.action.hover,
		},
	},
}))(TableRow);

const STATUS_COLOR = {
	'Approved': { background: '#e8f5e9', color: '#2e7d32' },
	'Pending Approval': { background: '#fff3e0', color: '#ed6c02' },
	'Rejected': { background: '#ffebee', color: '#d32f2f' },
	'Cancelled': { background: '#f5f5f5', color: '#616161' },
};

const StatusChip = ({ status }) => (
	<Chip
		label={status}
		size="small"
		style={{
			background: STATUS_COLOR[status]?.background || '#eee',
			color: STATUS_COLOR[status]?.color || '#333',
			fontWeight: 600,
		}}
	/>
);

// muted = this step is still "Pending" only because an earlier step already rejected the whole
// request, so it will never actually be acted on — shown faded instead of the normal live orange.
// A genuinely still-actionable Pending step (overall status still "Pending Approval") stays live.
const StepIcon = ({ status, muted }) => {
	if (status === 'Approved') return <CheckCircleIcon style={{ color: '#2e7d32' }} />
	if (status === 'Rejected') return <CancelIcon style={{ color: '#d32f2f' }} />
	return <HourglassEmptyIcon style={{ color: muted ? '#bbb' : '#ed6c02', opacity: muted ? 0.6 : 1 }} />
}

const AdhocDashboard = (props) => {
	const classes = useStyles();
	const [transactions, setTransactions] = useState([])
	const [totalCount, setTotalCount] = useState(0)
	const [type, setType] = useState('ALL')
	const [status, setStatus] = useState('ALL')
	// fromDate/toDate are the raw date-picker values (update as the user picks them); appliedFromDate/
	// appliedToDate are only committed — and only then trigger the fetch — once "Apply" is clicked.
	const [fromDate, setFromDate] = useState('')
	const [toDate, setToDate] = useState('')
	const [appliedFromDate, setAppliedFromDate] = useState('')
	const [appliedToDate, setAppliedToDate] = useState('')
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(10)
	const [stepsModal, setStepsModal] = useState(false)
	const [selectedTx, setSelectedTx] = useState(null)
	const [csvData, setCsvData] = useState([])
	const csvLinkRef = useRef(null)

	// Modal for both Approve and Reject — a remark is required for either action.
	const [actionModal, setActionModal] = useState({ open: false, tx: null, action: null })
	const [actionRemark, setActionRemark] = useState('')

	const buildFilterParams = () => {
		const params = {}
		if (type !== 'ALL') params.type = type
		if (status !== 'ALL') params.status = status
		if (appliedFromDate) params.fromDate = appliedFromDate
		if (appliedToDate) params.toDate = appliedToDate
		return params
	}

	const getAllAdhocTransactions = async () => {
		props.setLoading(true)
		try {
			const params = { ...buildFilterParams(), page: page + 1, limit: rowsPerPage }
			const result = await HttpService.getAllAdhocTransactions(params)
			setTransactions(result.data.data)
			setTotalCount(result.data.totalCount)
		} catch (error) {
			console.error(error)
			props.setSnackInfo("Couldn't fetch Ad-hoc PO/SO dashboard", "error")
		}
		props.setLoading(false)
	}

	// Backend-driven pagination/filtering: refetch whenever type, status, the applied date range,
	// or the page changes. Note: fromDate/toDate themselves are NOT in this list — only
	// appliedFromDate/appliedToDate (set by the "Apply" button) trigger a refetch.
	useEffect(() => {
		getAllAdhocTransactions()
		// eslint-disable-next-line
	}, [type, status, appliedFromDate, appliedToDate, page, rowsPerPage])

	// Switching a filter should always land back on page 1 of the new result set.
	useEffect(() => {
		setPage(0)
		// eslint-disable-next-line
	}, [type, status, appliedFromDate, appliedToDate])

	const applyDateFilter = () => {
		setAppliedFromDate(fromDate)
		setAppliedToDate(toDate)
	}

	const clearDateFilter = () => {
		setFromDate('')
		setToDate('')
		setAppliedFromDate('')
		setAppliedToDate('')
	}

	// Excel export ignores pagination — pulls every row matching the current Type/Status/Date
	// filters (limit=-1, same "All" mode the rows-per-page control already supports) rather than
	// just the rows visible on screen.
	const downloadExcel = async () => {
		props.setLoading(true)
		try {
			const params = { ...buildFilterParams(), limit: -1 }
			const result = await HttpService.getAllAdhocTransactions(params)
			const rows = result.data.data.map(tx => ({
				project: tx.project ? `${tx.project.code}_${tx.project.clientName}` : 'N/A',
				orderNumber: tx.orderNumber || '—',
				type: tx.type,
				amount: tx.amount || 0,
				status: tx.status,
				createdBy: tx.createdBy,
				pendingApprovalRole: tx.pendingApprovalRole || 'NA',
				createdDate: tx.createdDate ? moment(tx.createdDate).format('DD-MM-YYYY hh:mm A') : ''
			}))
			setCsvData(rows)
			setTimeout(() => csvLinkRef.current?.link.click())
		} catch (error) {
			console.error(error)
			props.setSnackInfo("Couldn't prepare Ad-hoc PO/SO excel export", "error")
		}
		props.setLoading(false)
	}


	const isMyTurn = (tx) => {
		return tx.status === 'Pending Approval' && tx.canAct
	}

	const handleApprove = async (tx, remark) => {
		props.setLoading(true)
		try {
			await HttpService.approveAdhocRequest(tx._id, { remark })
			props.setSnackInfo('Ad-hoc request approved', 'success')
			await getAllAdhocTransactions()
		} catch (error) {
			console.error(error)
			props.setSnackInfo(error?.response?.data?.message ?? "Couldn't approve request", 'error')
		}
		props.setLoading(false)
	}

	const handleReject = async (tx, remark) => {
		props.setLoading(true)
		try {
			await HttpService.rejectAdhocRequest(tx._id, { remark })
			props.setSnackInfo('Ad-hoc request rejected', 'success')
			await getAllAdhocTransactions()
		} catch (error) {
			console.error(error)
			props.setSnackInfo(error?.response?.data?.message ?? "Couldn't reject request", 'error')
		}
		props.setLoading(false)
	}

	const openActionModal = (tx, action) => {
		setActionModal({ open: true, tx, action })
		setActionRemark('')
	}

	const closeActionModal = () => {
		setActionModal({ open: false, tx: null, action: null })
		setActionRemark('')
	}

	// Remark is required for both Approve and Reject — same validation either way.
	const submitActionModal = async () => {
		if (!actionRemark.trim()) {
			return props.setSnackInfo('A remark is required', 'error')
		}
		const { tx, action } = actionModal
		if (action === 'approve') {
			await handleApprove(tx, actionRemark)
		} else {
			await handleReject(tx, actionRemark)
		}
		closeActionModal()
	}

	const openStepsModal = (tx) => {
		setSelectedTx(tx)
		setStepsModal(true)
	}

	// The Action column is only worth showing if the logged-in user has something to act on among
	// the rows currently on screen (data is paginated server-side, so this is per-page, not global).
	const hasActionableRows = transactions.some(isMyTurn)
	const columnCount = hasActionableRows ? 9 : 8

	const handleChangePage = (event, newPage) => {
		setPage(newPage)
	}

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10))
		setPage(0)
	}

	const activeStepIndex = selectedTx?.approvalSteps?.findIndex(step => step.status === 'Pending')
	const stepperActiveIndex = activeStepIndex === -1 ? (selectedTx?.approvalSteps?.length || 0) : activeStepIndex

	return (
		<div>
			{/* financeOverview/index.css forces .ant-modal/.ant-modal-content to 80vw and
			    .ant-modal-body to calc(100vh - 110px) with !important, and since that CSS is bundled
			    globally (statically imported, not lazy-loaded per route) it applies here too,
			    overriding any width/bodyStyle passed to <Modal> in JS. This scoped, higher-specificity
			    override (still !important, so it can actually win) keeps the Approve/Reject modal small. */}
			<style>{`
				.adhoc-action-modal .ant-modal,
				.adhoc-action-modal .ant-modal-content {
					width: 50vw !important;
				}
				.adhoc-action-modal .ant-modal-body {
					height: auto !important;
					max-height: 20vh !important;
					overflow-y: auto !important;
					padding: 16px !important;
				}
			`}</style>
			<Paper className={classes.paper}>
				<div className={classes.headerRow}>
					<h3 style={{ margin: 0 }}>Ad-hoc PO/SO Dashboard</h3>
					<div className={classes.filters}>
						<RadioGroup row value={type} onChange={(e) => setType(e.target.value)}>
							<FormControlLabel value="ALL" control={<Radio color="primary" />} label="All" />
							<FormControlLabel value="PO" control={<Radio color="primary" />} label="Ad-hoc PO" />
							<FormControlLabel value="SO" control={<Radio color="primary" />} label="Ad-hoc SO" />
						</RadioGroup>
						<FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
							<InputLabel id="adhoc-status-filter-label">Status</InputLabel>
							<Select
								labelId="adhoc-status-filter-label"
								value={status}
								onChange={(e) => setStatus(e.target.value)}
								label="Status"
							>
								<MenuItem value="ALL">All</MenuItem>
								<MenuItem value="Pending Approval">Pending Approval</MenuItem>
								<MenuItem value="Approved">Approved</MenuItem>
								<MenuItem value="Rejected">Rejected</MenuItem>
								<MenuItem value="Cancelled">Cancelled</MenuItem>
							</Select>
						</FormControl>
						<TextField
							label="From Date"
							type="date"
							size="small"
							variant="outlined"
							value={fromDate}
							onChange={(e) => setFromDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
						<TextField
							label="To Date"
							type="date"
							size="small"
							variant="outlined"
							value={toDate}
							onChange={(e) => setToDate(e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
						<Button
							variant="contained"
							size="small"
							style={{ background: "#F47E46", color: "#FFF2EB" }}
							onClick={applyDateFilter}
						>
							Submit
						</Button>
						{(fromDate || toDate || appliedFromDate || appliedToDate) &&
							<Button size="small" onClick={clearDateFilter}>Clear Dates</Button>
						}
						<CSVLink
							headers={CSV_HEADERS}
							filename={`AdhocPoSoDashboard_${moment().format('DD-MM-YYYY')}.csv`}
							data={csvData}
							ref={csvLinkRef}
						/>
						<Button
							variant="contained"
							size="small"
							style={{ background: "#355025", color: "#FFF2EB" }}
							startIcon={<GetAppIcon />}
							onClick={downloadExcel}
						>
							Download Excel
						</Button>
					</div>
				</div>
			</Paper>

			<TableContainer component={Paper}>
				<Table className={classes.table} size="small" stickyHeader aria-label="ad-hoc po so dashboard table">
					<TableHead>
						<TableRow>
							<StyledTableCell>Project</StyledTableCell>
							<StyledTableCell>PO/SO Number</StyledTableCell>
							<StyledTableCell align="center">Type</StyledTableCell>
							<StyledTableCell align="right">Amount</StyledTableCell>
							<StyledTableCell align="center">Status</StyledTableCell>
							<StyledTableCell>Created By</StyledTableCell>
							<StyledTableCell align="center">Approval Details</StyledTableCell>
							<StyledTableCell>Created Date</StyledTableCell>
							{/* Only shown when the logged-in user actually has a pending step to act on
							    among the visible rows — once every approver has acted (or it's not their
							    turn), the column disappears entirely instead of sitting there empty. */}
							{hasActionableRows && <StyledTableCell>Action</StyledTableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{transactions.length === 0 &&
							<TableRow>
								<StyledTableCell colSpan={columnCount} align="center" style={{ padding: 30, color: '#888' }}>
									No Ad-hoc {type === 'ALL' ? 'PO/SO' : type} requests found
								</StyledTableCell>
							</TableRow>
						}
						{transactions.map((tx, i) => (
							<StyledTableRow key={i}>
								<StyledTableCell>{tx.project ? `${tx.project.code}_${tx.project.clientName}` : 'N/A'}</StyledTableCell>
								<StyledTableCell>{tx.orderNumber || '—'}</StyledTableCell>
								<StyledTableCell align="center">
									<Chip label={tx.type} size="small" variant="outlined" style={{ fontWeight: 600 }} />
								</StyledTableCell>
								<StyledTableCell align="right">₹{Number(tx.amount || 0).toLocaleString('en-IN')}</StyledTableCell>
								<StyledTableCell align="center"><StatusChip status={tx.status} /></StyledTableCell>
								<StyledTableCell>{tx.createdBy}</StyledTableCell>
								<StyledTableCell align="center">
									<Button
										size="small"
										variant="outlined"
										startIcon={<VisibilityIcon />}
										onClick={() => openStepsModal(tx)}
									>
										View
									</Button>
								</StyledTableCell>
								<StyledTableCell>{moment(tx.createdDate).format('DD-MM-YYYY hh:mm A')}</StyledTableCell>
								{hasActionableRows &&
									<StyledTableCell>
										{isMyTurn(tx) && <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
											<Button size="small" variant="contained" style={{ background: "#355025", color: "#FFF2EB" }} onClick={() => openActionModal(tx, 'approve')}>Approve</Button>
											<Button size="small" variant="contained" color="secondary" onClick={() => openActionModal(tx, 'reject')}>Reject</Button>
										</div>}
									</StyledTableCell>
								}
							</StyledTableRow>
						))}
					</TableBody>
				</Table>
				<TablePagination
					rowsPerPageOptions={[10, 25, 50, { value: -1, label: 'All' }]}
					component="div"
					count={totalCount}
					rowsPerPage={rowsPerPage}
					page={rowsPerPage === -1 ? 0 : page}
					onChangePage={handleChangePage}
					onChangeRowsPerPage={handleChangeRowsPerPage}
				/>
			</TableContainer>

			<Modal
				title={selectedTx ? `Approval Steps — Ad-hoc ${selectedTx.type} · ${selectedTx.project ? `${selectedTx.project.code}_${selectedTx.project.clientName}` : ''}` : 'Approval Steps'}
				centered
				visible={stepsModal}
				onCancel={() => setStepsModal(false)}
				width={400}
				bodyStyle={{ maxHeight: '60vh', overflowY: 'auto', padding: 16 }}
				footer={null}
			>
				{selectedTx && <>
					<Grid container style={{ marginBottom: 10 }}>
						<Grid item xs={6}><b>Amount: </b>₹{Number(selectedTx.amount || 0).toLocaleString('en-IN')}</Grid>
						<Grid item xs={6}><b>Overall Status: </b><StatusChip status={selectedTx.status} /></Grid>
						<Grid item xs={6} style={{ marginTop: 8 }}>
							<b>Expected Delivery Date: </b>
							{selectedTx.expectedDeliveryDate ? moment(selectedTx.expectedDeliveryDate).format('DD-MM-YYYY') : '—'}
						</Grid>
						<Grid item xs={12} style={{ marginTop: 8 }}>
							<b>Remark: </b>{selectedTx.reason || '—'}
						</Grid>
					</Grid>

					{selectedTx.materials?.length > 0 &&
						<TableContainer component={Paper} variant="outlined" style={{ marginBottom: 16, borderRadius: 6, overflow: 'hidden' }}>
							<Table size="small">
								<TableHead>
									<TableRow>
										<StyledTableCell>Item</StyledTableCell>
										<StyledTableCell align="right">Quantity</StyledTableCell>
										<StyledTableCell align="right">Rate</StyledTableCell>
										<StyledTableCell align="right">Amount</StyledTableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{selectedTx.materials.map((mat, k) => (
										<StyledTableRow key={k}>
											<StyledTableCell>{mat.itemName}{mat.unit ? ` (${mat.unit})` : ''}</StyledTableCell>
											<StyledTableCell align="right">{mat.quantity}</StyledTableCell>
											<StyledTableCell align="right">₹{Number(mat.rate || 0).toLocaleString('en-IN')}</StyledTableCell>
											<StyledTableCell align="right">₹{Number(mat.amount || 0).toLocaleString('en-IN')}</StyledTableCell>
										</StyledTableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					}

					<TableContainer component={Paper} variant="outlined" style={{ marginBottom: 16, borderRadius: 6, overflow: 'hidden' }}>
						<Table size="small">
							<TableBody>
								<TableRow>
									<TableCell style={{ border: 'none' }}>Item Value</TableCell>
									<TableCell align="right" style={{ border: 'none', fontVariantNumeric: 'tabular-nums' }}>
										₹{Number(selectedTx.itemValue || 0).toLocaleString('en-IN')}
									</TableCell>
								</TableRow>
								{selectedTx.type === 'PO' && !!selectedTx.discountAmount &&
									<TableRow>
										<TableCell style={{ border: 'none' }}>Discount ({selectedTx.discountPercent || 0}%)</TableCell>
										<TableCell align="right" style={{ border: 'none', color: '#d32f2f', fontVariantNumeric: 'tabular-nums' }}>
											− ₹{Number(selectedTx.discountAmount || 0).toLocaleString('en-IN')}
										</TableCell>
									</TableRow>
								}
								{selectedTx.type === 'PO' && !!selectedTx.freight &&
									<TableRow>
										<TableCell style={{ border: 'none' }}>Freight</TableCell>
										<TableCell align="right" style={{ border: 'none', color: '#2e7d32', fontVariantNumeric: 'tabular-nums' }}>
											+ ₹{Number(selectedTx.freight || 0).toLocaleString('en-IN')}
										</TableCell>
									</TableRow>
								}
								{selectedTx.gstRequired &&
									<TableRow>
										<TableCell style={{ border: 'none' }}>GST ({selectedTx.gstDetails?.gst} {selectedTx.gstDetails?.rate}%)</TableCell>
										<TableCell align="right" style={{ border: 'none', color: '#2e7d32', fontVariantNumeric: 'tabular-nums' }}>
											+ ₹{Number(selectedTx.gstAmount || 0).toLocaleString('en-IN')}
										</TableCell>
									</TableRow>
								}
								<TableRow style={{ background: '#EAF0F8' }}>
									<TableCell style={{ border: 'none', fontWeight: 700, color: '#1C375D' }}>Cumulative Value</TableCell>
									<TableCell align="right" style={{ border: 'none', fontWeight: 700, color: '#1C375D', fontSize: 15, fontVariantNumeric: 'tabular-nums' }}>
										₹{Number(selectedTx.cumulativeValue || 0).toLocaleString('en-IN')}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>

					<Stepper activeStep={stepperActiveIndex} orientation="vertical">
						{selectedTx.approvalSteps?.map((step, j) => {
							// Only the one step that's actually next-in-line right now — the first
							// "Pending" step, while the overall request is still "Pending Approval" —
							// is genuinely awaiting someone, so only that one stays live orange. Every
							// other "Pending" step is muted: either it's further down the chain and
							// hasn't been reached yet, or it's moot because an earlier step already
							// rejected the whole request and this step will never happen.
							const isCurrentActionable = step.status === 'Pending' && selectedTx.status === 'Pending Approval' && j === activeStepIndex
							const isMutedPending = step.status === 'Pending' && !isCurrentActionable
							return (
								<Step key={j} completed={step.status === 'Approved'}>
									<StepLabel
										StepIconComponent={() => <StepIcon status={step.status} muted={isMutedPending} />}
										error={step.status === 'Rejected'}
									>
										<span style={isMutedPending ? { color: '#999', opacity: 0.6 } : undefined}>
											<b>{step.role}</b> — {step.status}
										</span>
									</StepLabel>
									{/* This modal is a full timeline, not a step-by-step wizard, so every step's
									    content (approver/time) must stay visible — not just the active step's.
									    MUI's StepContent collapses non-active steps by default; forcing
									    TransitionProps.in keeps them all expanded. */}
									<StepContent TransitionProps={{ in: true }}>
										{step.status === 'Approved' && step.approverName &&
											<div style={{ color: '#2e7d32', fontWeight: 600 }}>
												Approved by {step.approverName}
											</div>
										}
										{step.status === 'Rejected' && step.approverName &&
											<div style={{ color: '#d32f2f', fontWeight: 600 }}>
												Rejected by {step.approverName}
											</div>
										}
										{step.decisionDate && (step.status === 'Approved' || step.status === 'Rejected') &&
											<div style={{ color: '#666' }}>on {moment(step.decisionDate).format('DD-MM-YYYY, hh:mm A')}</div>
										}
										{step.remark && <div>Remark: {step.remark}</div>}
										{step.status === 'Pending' &&
											<div style={{ color: isMutedPending ? '#999' : '#ed6c02', opacity: isMutedPending ? 0.6 : 1 }}>
												Awaiting action
											</div>
										}
									</StepContent>
								</Step>
							)
						})}
					</Stepper>
				</>}
			</Modal>

			<Modal
				title={actionModal.action === 'approve' ? 'Approve Ad-hoc Request' : 'Reject Ad-hoc Request'}
				centered
				visible={actionModal.open}
				onCancel={closeActionModal}
				wrapClassName="adhoc-action-modal"
				width={400}
				bodyStyle={{ maxHeight: '20vh', overflowY: 'auto', padding: 16 }}
				footer={null}
				destroyOnClose
			>
				<TextField
					label="Remark"
					required
					autoFocus
					multiline
					minRows={2}
					fullWidth
					variant="outlined"
					value={actionRemark}
					onChange={(e) => setActionRemark(e.target.value)}
					style={{ marginTop: 6, marginBottom: 6 }}
				/>
				<div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
					<Button onClick={closeActionModal}>Cancel</Button>
					<Button
						variant="contained"
						color={actionModal.action === 'reject' ? 'secondary' : undefined}
						style={actionModal.action === 'approve' ? { background: "#355025", color: "#FFF2EB" } : undefined}
						onClick={submitActionModal}
					>
						Submit
					</Button>
				</div>
			</Modal>
		</div>
	)
}

export default connect(null, { setLoading, setSnackInfo })(AdhocDashboard)
